// src/lib/history-trail.ts
//
// Guarda o caminho de cada entrada do histórico desta aba, indexado pela
// posição do TanStack (`__TSR_index`). Permite que um botão "Voltar" recue até
// a tela de origem pulando entradas que pertencem à mesma área (ex.: aulas e
// abas de um curso), em vez de apontar para uma rota fixa.
import type { MouseEvent } from "react";
import { useRouter, type RouterHistory } from "@organizacaox/lai-design-system/router";

const trails = new WeakMap<RouterHistory, Map<number, string>>();

function currentIndex(history: RouterHistory) {
  return history.location.state.__TSR_index ?? 0;
}

export function trackHistory(history: RouterHistory) {
  if (trails.has(history)) return;
  const trail = new Map<number, string>();
  trails.set(history, trail);
  const record = () => trail.set(currentIndex(history), history.location.pathname);
  record();
  history.subscribe(record);
}

// Quantos passos recuar até a primeira entrada fora da área. Entradas
// anteriores ao rastreio (antes de recarregar a página) contam como fora.
// `null` quando não há tela anterior no app: o chamador usa o destino padrão.
export function stepsBackOutside(
  history: RouterHistory,
  isInside: (pathname: string) => boolean,
): number | null {
  const trail = trails.get(history);
  const current = currentIndex(history);
  for (let index = current - 1; index >= 0; index--) {
    const pathname = trail?.get(index);
    if (pathname === undefined || !isInside(pathname)) return current - index;
  }
  return null;
}

// Handler de clique para um <Link> "Voltar": recua no histórico até a tela de
// origem; sem origem no app (ou clique com modificador), o link segue o `to`.
export function useHistoryBack(isInside: (pathname: string) => boolean) {
  const router = useRouter();
  return (event: MouseEvent) => {
    if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey)
      return;
    const steps = stepsBackOutside(router.history, isInside);
    if (steps === null) return;
    event.preventDefault();
    router.history.go(-steps);
  };
}
